"use client";

import { useTranslations } from "use-intl";

import {
  ActiveNotificationIcon,
  CheckIcon,
  FavoriteIcon,
  FilterSettingsIcon,
  LockIcon,
  MoreIcon,
  OpenExternalIcon,
  PriceChangeIcon,
} from "@/components/appIcons";
import { TelegramIcon } from "@/components/icons/telegram-icon";
import { TelegramCompactConnectionAction } from "@/components/telegram/TelegramCompactConnectionAction";
import { useTelegramConnectionAction } from "@/hooks/use-telegram-connection-action";
import { cn } from "@/lib/utils";

const BENEFITS = [
  { icon: ActiveNotificationIcon, key: "directAlerts" },
  { icon: FavoriteIcon, key: "tasteMatched" },
  { icon: FilterSettingsIcon, key: "manageAnytime" },
] as const;

interface TelegramConversionShowcaseProps {
  callbackURL?: string;
  className?: string;
}

/** Reusable conversion section that demonstrates Garimpa's Telegram alerts in context. */
export function TelegramConversionShowcase({
  callbackURL = "/telegram",
  className,
}: TelegramConversionShowcaseProps) {
  const t = useTranslations("telegram");
  const connection = useTelegramConnectionAction(callbackURL);

  return (
    <section
      className={cn(
        "relative isolate overflow-hidden bg-[#e8f1f7] selection:bg-[#229ed9] selection:text-white dark:bg-[#0b141a]",
        className,
      )}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-60 [background-image:radial-gradient(circle_at_12%_18%,rgba(34,158,217,0.18),transparent_28%),radial-gradient(circle_at_88%_82%,rgba(251,146,60,0.12),transparent_26%)] dark:opacity-35"
      />

      <div className="mx-auto grid min-h-[calc(100dvh-3.5rem)] w-full max-w-[1280px] items-center gap-10 px-4 py-10 sm:px-6 md:py-14 lg:grid-cols-[minmax(0,0.9fr)_minmax(32rem,1.1fr)] lg:gap-x-16 lg:px-10 xl:gap-x-20">
        <div>
          <h1 className="max-w-2xl text-balance text-[2.55rem] font-bold leading-[1.02] tracking-[-0.035em] text-[#10212b] sm:text-5xl lg:text-[3.65rem] dark:text-white">
            {t("headline")}
          </h1>
          <p className="mt-5 max-w-xl text-pretty text-base leading-relaxed text-[#46606f] sm:text-lg dark:text-[#a9bdc8]">
            {t("description")}
          </p>

          <div className="mt-7">
            <TelegramCompactConnectionAction {...connection} label={t("heroConnect")} />
            <p className="mt-3 flex max-w-md items-start gap-2 text-xs leading-relaxed text-[#5b7380] dark:text-[#91a8b4]">
              <OpenExternalIcon decorative className="mt-0.5 text-[16px]" />
              {t("handoffNote")}
            </p>
          </div>

          <ul className="mt-8 space-y-3 text-sm font-semibold text-[#263e4b] dark:text-[#d5e2e8]">
            {BENEFITS.map(({ icon, key }) => {
              const Icon = icon;
              return (
                <li key={key} className="flex items-center gap-3">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white text-[#168ac2] shadow-[0_6px_18px_rgba(24,55,73,0.09)] dark:bg-[#17242c] dark:text-[#63b9e6]">
                    <Icon className="text-[18px]" />
                  </span>
                  {t(key)}
                </li>
              );
            })}
          </ul>
        </div>

        <figure
          aria-label={t("liveAlertLabel")}
          className="mx-auto w-full max-w-[42rem] overflow-hidden rounded-[1.5rem] bg-[#0e1621] shadow-[0_28px_70px_rgba(13,35,48,0.3)] ring-1 ring-black/10 dark:shadow-[0_30px_80px_rgba(0,0,0,0.45)]"
        >
          <div className="flex items-center gap-3 bg-[#202c33] px-4 py-3.5 text-white sm:px-5">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#229ed9]">
              <TelegramIcon className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold">Garimpa.Club</p>
              <p className="truncate text-xs text-[#8fb6ca]">{t("chatStatus")}</p>
            </div>
            <MoreIcon decorative className="text-[#8fa3ad]" />
          </div>

          <div className="telegram-chat-wallpaper space-y-2 px-3 py-3 sm:space-y-2.5 sm:px-5 sm:py-4">
            <div className="ml-auto max-w-[84%] rounded-[14px] rounded-br-[4px] bg-[#2b5278] px-3.5 py-2.5 text-sm leading-relaxed text-white shadow-[0_3px_10px_rgba(0,0,0,0.16)]">
              {t("messageUserRequest")}
              <span className="ml-2 whitespace-nowrap text-[10px] tabular-nums text-[#a8c8df]">
                10:13 <CheckIcon decorative className="text-[12px]" />
              </span>
            </div>

            <div className="max-w-[84%] rounded-[14px] rounded-bl-[4px] bg-[#182533] px-3.5 py-2.5 text-sm leading-relaxed text-[#edf5f8] shadow-[0_3px_10px_rgba(0,0,0,0.16)]">
              {t("messageBotReply")}
              <span className="ml-2 whitespace-nowrap text-[10px] tabular-nums text-[#8fa3ad]">
                10:13
              </span>
            </div>

            <div className="telegram-offer-arrive max-w-[94%] overflow-hidden rounded-[14px] rounded-bl-[4px] bg-[#182533] shadow-[0_12px_30px_rgba(0,0,0,0.28)] sm:max-w-[88%]">
              <div className="flex items-center gap-2 border-b border-white/8 px-3.5 py-2.5 text-xs font-bold text-[#63b9e6]">
                <PriceChangeIcon decorative className="text-[18px]" />
                {t("offerExample")}
                <span className="ml-auto rounded-full bg-[#229ed9]/15 px-2 py-1 text-[10px] uppercase tracking-[0.08em] text-[#8ed2f3]">
                  {t("liveAlertLabel")}
                </span>
              </div>

              <div className="grid grid-cols-[5.75rem_1fr] gap-3 p-3.5 sm:grid-cols-[7rem_1fr] sm:gap-4">
                <div className="relative aspect-square overflow-hidden rounded-[10px] bg-[#d8a77b] shadow-[0_8px_20px_rgba(0,0,0,0.3)]">
                  <div className="absolute inset-x-0 top-0 h-[45%] bg-[#d9c5a6]" />
                  <div className="absolute -bottom-7 -left-6 size-24 rounded-full bg-[#e45b4f] sm:size-28" />
                  <div className="absolute -bottom-9 -right-7 size-24 rounded-full bg-[#264653] sm:size-28" />
                  <div className="absolute inset-x-2 top-2 text-[8px] font-bold tracking-[0.16em] text-[#2e2926] sm:text-[9px]">
                    PINK FLOYD
                  </div>
                  <div className="absolute inset-x-2 bottom-2 text-[7px] font-bold uppercase leading-tight tracking-[0.12em] text-white sm:text-[8px]">
                    Wish You Were Here
                  </div>
                </div>

                <div className="min-w-0">
                  <p className="text-sm font-bold leading-snug text-white sm:text-base">
                    {t("offerAlbum")}
                  </p>
                  <p className="mt-1 text-xs text-[#9db0ba]">{t("offerEdition")}</p>
                  <div className="mt-3 flex flex-wrap items-end gap-x-2 gap-y-1">
                    <span className="text-xl font-bold tabular-nums text-white sm:text-2xl">
                      {t("offerPrice")}
                    </span>
                    <span className="pb-0.5 text-xs tabular-nums text-[#8296a1] line-through">
                      {t("offerPreviousPrice")}
                    </span>
                    <span className="rounded-full bg-[#2ea66b]/20 px-2 py-1 text-[10px] font-bold text-[#6dd59c]">
                      {t("offerDrop")}
                    </span>
                  </div>
                  <p className="mt-2 truncate text-xs font-semibold text-[#c2d0d6]">
                    {t("offerStore")}
                  </p>
                  <p className="mt-0.5 text-[11px] text-[#8296a1]">{t("offerShipping")}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#202c33] px-3.5 pb-[calc(5.25rem+env(safe-area-inset-bottom))] pt-3.5 sm:px-4 sm:pt-4 lg:p-4">
            <TelegramCompactConnectionAction
              {...connection}
              label={t("offerConnect")}
              className="sm:w-full"
            />
            <p className="mt-3 flex items-start gap-2 text-[11px] leading-relaxed text-[#9eb1bb]">
              <LockIcon decorative className="mt-0.5 text-[15px] text-[#63b9e6]" />
              {t("privacyReassurance")}
            </p>
          </div>
        </figure>
      </div>
    </section>
  );
}
